import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

export default function HandymanServicesCostBoiseIdaho() {
  useSEO({
    title: 'Handyman Services Cost in Boise, Idaho (2025 Pricing Guide)',
    description: 'Complete handyman pricing guide for Boise, Idaho. Hourly rates, common job costs, and when to hire a handyman vs contractor in the Treasure Valley.',
    keywords: 'handyman cost Boise Idaho, handyman services Treasure Valley, handyman rates Boise, home repair cost Meridian, handyman vs contractor Idaho',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/blog/handyman-services-cost-boise-idaho`,
    ogType: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'Handyman Services Cost in Boise, Idaho (2025 Pricing Guide)',
          datePublished: '2025-01-15',
          dateModified: '2025-01-15',
          author: {
            '@type': 'Organization',
            name: 'Fix Right Services',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Fix Right Services',
            logo: {
              '@type': 'ImageObject',
              url: 'https://static.readdy.ai/fixrightservices/logo.svg',
            },
          },
          image: 'https://public.readdy.ai/ai/img_res/14e6a7a0ed0d08dace9b687ce0f0b958.jpg',
          keywords: 'handyman cost Boise Idaho, handyman services Treasure Valley, handyman rates Boise',
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How much does a handyman cost in Boise, Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Handyman services in Boise typically cost $60-$90 per hour, with an average of $75/hour. Many handymen charge a minimum service call fee of $100-$150 for the first hour. Rates vary based on experience, job complexity, and whether materials are included.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is the average cost for common handyman jobs in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Common handyman job costs in Boise: drywall repair $150-$400, door installation $200-$500, deck repair $300-$800, gutter cleaning $150-$300, light fixture installation $100-$250, and fence repair $200-$600. Costs depend on job size and materials.',
              },
            },
            {
              '@type': 'Question',
              name: 'When should I hire a handyman vs. a contractor in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Hire a handyman for small repairs, minor installations, and jobs under $1,000 that don\'t require permits. Hire a licensed contractor for major renovations, structural work, electrical/plumbing systems, and any project requiring permits in Boise.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do handymen in Boise charge by the hour or by the job?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Most Boise handymen charge hourly ($60-$90/hour) for small or unpredictable jobs. For larger, well-defined projects, many offer flat-rate pricing. Always get a written estimate before work begins to avoid surprises.',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I get an accurate handyman estimate in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'For accurate estimates: provide detailed descriptions with photos, list all tasks needed, specify materials (you provide vs. handyman provides), ask about minimum charges and travel fees, and get written quotes from 2-3 handymen. Be specific about scope to avoid change orders.',
              },
            },
            {
              '@type': 'Question',
              name: 'Are handymen in Boise licensed and insured?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Idaho doesn\'t require handyman licenses for jobs under $2,000, but reputable handymen carry general liability insurance. For jobs over $2,000 or specialized work (electrical, plumbing), hire a licensed contractor. Always verify insurance before hiring.',
              },
            },
          ],
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://fixrightservices.org/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: 'https://fixrightservices.org/blog',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Handyman Services Cost in Boise, Idaho (2025 Pricing Guide)',
              item: 'https://fixrightservices.org/blog/handyman-services-cost-boise-idaho',
            },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center gap-2 cursor-pointer">
              <img src="/images/logo.svg" alt="Fix Right Services" className="h-10 w-auto" />
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/#services" className="text-gray-700 hover:text-[#B45309] font-medium cursor-pointer whitespace-nowrap">
                Services
              </a>
              <a href="/blog" className="text-[#B45309] font-medium cursor-pointer whitespace-nowrap">
                Blog
              </a>
              <a href="/#contact" className="bg-[#B45309] text-white px-6 py-2.5 rounded-lg hover:bg-[#92400E] font-semibold cursor-pointer whitespace-nowrap">
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#B45309]/5 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-6">
            <a href="/" className="text-gray-500 hover:text-[#B45309] cursor-pointer">Home</a>
            <Icon name="arrow-right" className="w-3 h-3 text-gray-400" />
            <a href="/blog" className="text-gray-500 hover:text-[#B45309] cursor-pointer">Blog</a>
            <Icon name="arrow-right" className="w-3 h-3 text-gray-400" />
            <span className="text-gray-900">Handyman Services Cost</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Handyman Services Cost in Boise, Idaho (2025 Pricing Guide)
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
            <span className="flex items-center gap-2">
              <Icon name="calendar" className="w-4 h-4" />
              January 15, 2025
            </span>
            <span className="flex items-center gap-2">
              <Icon name="time" className="w-4 h-4" />
              9 min read
            </span>
          </div>
          <img
            src="https://readdy.ai/api/search-image?query=professional%20handyman%20working%20residential%20home%20boise%20idaho%20tool%20belt%20fixing%20repairs%20maintenance%20clean%20background%20skilled%20tradesman%20home%20improvement%20high%20quality%20photography&width=1200&height=630&seq=handyman-cost-hero&orientation=landscape"
            alt="Handyman Services in Boise Idaho"
            className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
            loading="eager"
          />
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            From fixing a leaky faucet to installing shelves, handyman services help Boise homeowners tackle those nagging home repairs without the cost of a specialized contractor. Understanding handyman pricing helps you budget effectively and know when to hire a handyman versus a licensed contractor.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Average Handyman Rates in Boise</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Handyman services in Boise typically cost <strong>$60-$90 per hour</strong>, with most handymen charging around <strong>$75 per hour</strong>. Many also charge a minimum service call fee.
          </p>
          <div className="bg-[#B45309]/5 border-l-4 border-[#B45309] p-6 rounded-r-lg mb-8">
            <p className="text-gray-800 font-semibold mb-2">2025 Boise Handyman Pricing:</p>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Hourly Rate:</strong> $60-$90/hour (average $75/hour)</li>
              <li>• <strong>Minimum Service Call:</strong> $100-$150 (typically 1-2 hours)</li>
              <li>• <strong>Half-Day Rate:</strong> $300-$400 (4 hours)</li>
              <li>• <strong>Full-Day Rate:</strong> $500-$700 (8 hours)</li>
            </ul>
          </div>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Note:</strong> Rates vary based on handyman experience, job complexity, and whether materials are included. Licensed handymen with specialized skills (electrical, plumbing) charge $80-$100+ per hour.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Handyman Job Costs in Boise</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Here's what you can expect to pay for typical handyman services in the Treasure Valley:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Drywall Repair</h4>
                <span className="font-bold text-[#B45309]">$150 - $400</span>
              </div>
              <p className="text-sm text-gray-700">Small holes: $150-$200 | Large holes or water damage: $250-$400 | Includes patching, sanding, and painting</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Door Installation or Repair</h4>
                <span className="font-bold text-[#B45309]">$200 - $500</span>
              </div>
              <p className="text-sm text-gray-700">Interior door installation: $200-$350 | Exterior door: $350-$500 | Door hardware replacement: $100-$150</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Deck Repair</h4>
                <span className="font-bold text-[#B45309]">$300 - $800</span>
              </div>
              <p className="text-sm text-gray-700">Board replacement: $300-$500 | Railing repair: $200-$400 | Staining/sealing: $400-$800 (depends on deck size)</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Gutter Cleaning & Repair</h4>
                <span className="font-bold text-[#B45309]">$150 - $300</span>
              </div>
              <p className="text-sm text-gray-700">Cleaning: $150-$250 | Minor repairs: $100-$200 | Downspout installation: $150-$300</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Light Fixture Installation</h4>
                <span className="font-bold text-[#B45309]">$100 - $250</span>
              </div>
              <p className="text-sm text-gray-700">Simple replacement: $100-$150 | Ceiling fan installation: $150-$250 | Chandelier: $200-$300</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Fence Repair</h4>
                <span className="font-bold text-[#B45309]">$200 - $600</span>
              </div>
              <p className="text-sm text-gray-700">Post replacement: $200-$350 | Panel replacement: $150-$300 | Gate repair: $150-$250</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Shelf or Cabinet Installation</h4>
                <span className="font-bold text-[#B45309]">$100 - $300</span>
              </div>
              <p className="text-sm text-gray-700">Floating shelves: $100-$200 | Closet organizer: $200-$400 | TV mounting: $150-$250</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Painting (Interior)</h4>
                <span className="font-bold text-[#B45309]">$300 - $800</span>
              </div>
              <p className="text-sm text-gray-700">Single room: $300-$500 | Multiple rooms: $500-$800+ | Includes prep, painting, and cleanup</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Tile Repair or Replacement</h4>
                <span className="font-bold text-[#B45309]">$200 - $500</span>
              </div>
              <p className="text-sm text-gray-700">Small area (under 10 sq ft): $200-$350 | Larger area: $350-$500+ | Includes grout repair</p>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900">Window Screen Repair</h4>
                <span className="font-bold text-[#B45309]">$75 - $150</span>
              </div>
              <p className="text-sm text-gray-700">Screen replacement: $75-$100 per screen | Frame repair: $50-$100 | Multiple screens: discounts available</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Handyman vs. Contractor: When to Hire Which</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Understanding the difference between handymen and licensed contractors helps you hire the right professional and avoid legal issues.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#B45309]/5 p-6 rounded-lg border-2 border-[#B45309]">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="tools" className="w-5 h-5 text-[#B45309]" />
                Hire a Handyman For:
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• <strong>Small repairs</strong> (drywall holes, door adjustments)</li>
                <li>• <strong>Minor installations</strong> (shelves, fixtures, hardware)</li>
                <li>• <strong>Maintenance tasks</strong> (gutter cleaning, caulking)</li>
                <li>• <strong>Cosmetic updates</strong> (painting, trim work)</li>
                <li>• <strong>Jobs under $2,000</strong> (Idaho threshold)</li>
                <li>• <strong>No permits required</strong></li>
                <li>• <strong>Quick turnaround needed</strong> (1-2 days)</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-300">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="user-star" className="w-5 h-5 text-gray-600" />
                Hire a Licensed Contractor For:
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• <strong>Major renovations</strong> (kitchen, bathroom remodels)</li>
                <li>• <strong>Structural work</strong> (removing walls, foundation)</li>
                <li>• <strong>Electrical systems</strong> (panel upgrades, rewiring)</li>
                <li>• <strong>Plumbing systems</strong> (water heaters, re-piping)</li>
                <li>• <strong>Jobs over $2,000</strong> (Idaho law requirement)</li>
                <li>• <strong>Permit-required work</strong> (Boise building codes)</li>
                <li>• <strong>Warranty protection needed</strong></li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-8">
            <p className="text-gray-800 font-semibold mb-2">⚠️ Idaho Law:</p>
            <p className="text-gray-700">In Idaho, anyone performing construction work over $2,000 must hold a valid contractor license. Hiring unlicensed contractors for jobs over this threshold can void your homeowner's insurance and leave you liable for injuries or poor workmanship.</p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How to Get an Accurate Handyman Estimate</h2>
          <div className="space-y-4 mb-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Provide Detailed Descriptions</h4>
                <p className="text-gray-700">Include photos, measurements, and specific details about the problem. The more information you provide, the more accurate the estimate.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">List All Tasks</h4>
                <p className="text-gray-700">Create a comprehensive list of everything you need done. Handymen often offer discounts for multiple tasks completed in one visit.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">3</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Clarify Materials</h4>
                <p className="text-gray-700">Specify whether you're providing materials or if the handyman should purchase them. Handymen typically mark up materials 10-20%.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">4</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Ask About Minimums</h4>
                <p className="text-gray-700">Confirm minimum service charges and travel fees. Some Boise handymen charge extra for jobs in outlying areas like Kuna or Star.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">5</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Get Written Quotes</h4>
                <p className="text-gray-700">Always get estimates in writing with scope of work, timeline, and total cost. Compare quotes from 2-3 handymen before deciding.</p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Tips for Saving Money on Handyman Services</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Bundle Multiple Tasks</h4>
              <p className="text-sm text-gray-700">Combine several small jobs into one visit to maximize the minimum service fee and potentially negotiate a discount.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Provide Your Own Materials</h4>
              <p className="text-sm text-gray-700">Purchase materials yourself to avoid markup. Ensure you buy the correct items to prevent delays.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Schedule Off-Peak Times</h4>
              <p className="text-sm text-gray-700">Book during slower seasons (winter) or mid-week for potentially better rates and faster scheduling.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Do Prep Work Yourself</h4>
              <p className="text-sm text-gray-700">Clear the work area, move furniture, and handle simple prep tasks to reduce billable hours.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6 mb-12">
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">How much does a handyman cost in Boise, Idaho?</h4>
              <p className="text-gray-700">Handyman services in Boise typically cost $60-$90 per hour, with an average of $75/hour. Many handymen charge a minimum service call fee of $100-$150 for the first hour. Rates vary based on experience, job complexity, and whether materials are included.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">What is the average cost for common handyman jobs in Boise?</h4>
              <p className="text-gray-700">Common handyman job costs in Boise: drywall repair $150-$400, door installation $200-$500, deck repair $300-$800, gutter cleaning $150-$300, light fixture installation $100-$250, and fence repair $200-$600. Costs depend on job size and materials.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">When should I hire a handyman vs. a contractor in Idaho?</h4>
              <p className="text-gray-700">Hire a handyman for small repairs, minor installations, and jobs under $1,000 that don't require permits. Hire a licensed contractor for major renovations, structural work, electrical/plumbing systems, and any project requiring permits in Boise.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Do handymen in Boise charge by the hour or by the job?</h4>
              <p className="text-gray-700">Most Boise handymen charge hourly ($60-$90/hour) for small or unpredictable jobs. For larger, well-defined projects, many offer flat-rate pricing. Always get a written estimate before work begins to avoid surprises.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">How do I get an accurate handyman estimate in Boise?</h4>
              <p className="text-gray-700">For accurate estimates: provide detailed descriptions with photos, list all tasks needed, specify materials (you provide vs. handyman provides), ask about minimum charges and travel fees, and get written quotes from 2-3 handymen. Be specific about scope to avoid change orders.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Are handymen in Boise licensed and insured?</h4>
              <p className="text-gray-700">Idaho doesn't require handyman licenses for jobs under $2,000, but reputable handymen carry general liability insurance. For jobs over $2,000 or specialized work (electrical, plumbing), hire a licensed contractor. Always verify insurance before hiring.</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#B45309] to-[#92400E] text-white p-8 rounded-xl mb-12">
            <h3 className="text-2xl font-bold mb-3">Need Handyman Services in Boise?</h3>
            <p className="mb-6 text-white/90">Get a free quote from Fix Right Services. Licensed, insured, and experienced in all types of home repairs throughout the Treasure Valley.</p>
            <a href="/#contact" className="inline-flex items-center gap-2 bg-white text-[#B45309] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 cursor-pointer whitespace-nowrap">
              Get Free Quote
              <Icon name="arrow-right" className="w-4 h-4" />
            </a>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Related Articles</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <a href="/blog/handyman-vs-contractor-idaho" className="block bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
              <h4 className="font-bold text-gray-900 mb-2">When to Hire a Handyman vs. a Contractor in Idaho</h4>
              <p className="text-sm text-gray-600 mb-3">Complete guide to choosing between handyman services and licensed contractors for your Boise home project.</p>
              <span className="text-[#B45309] font-semibold text-sm flex items-center gap-2">
                Read More
                <Icon name="arrow-right" className="w-3.5 h-3.5" />
              </span>
            </a>
            <a href="/blog/seasonal-home-maintenance-treasure-valley" className="block bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
              <h4 className="font-bold text-gray-900 mb-2">Seasonal Home Maintenance Checklist for Treasure Valley</h4>
              <p className="text-sm text-gray-600 mb-3">Month-by-month maintenance guide to keep your Boise home in top condition year-round.</p>
              <span className="text-[#B45309] font-semibold text-sm flex items-center gap-2">
                Read More
                <Icon name="arrow-right" className="w-3.5 h-3.5" />
              </span>
            </a>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#1F2937] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="/images/logo.svg" alt="Fix Right Services" className="h-10 mb-4 brightness-0 invert" />
              <p className="text-gray-300 text-sm">Professional handyman services for all your home repair needs in Boise, Idaho.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><a href="/#services" className="hover:text-white cursor-pointer">Handyman Services</a></li>
                <li><a href="/#services" className="hover:text-white cursor-pointer">Home Repairs</a></li>
                <li><a href="/#services" className="hover:text-white cursor-pointer">Maintenance</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><a href="/blog" className="hover:text-white cursor-pointer">Blog</a></li>
                <li><a href="/#contact" className="hover:text-white cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <p className="text-sm text-gray-300 mb-2">Boise, Idaho</p>
              <p className="text-sm text-gray-300">(208) 123-4567</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}